import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { formatCurrency } from "@/lib/format-data";
import { useBookingEvent } from "./use-booking-event";
import { selfScheduleQueryOptions } from "@/api/check-in/self-schedule/self-schedule-query";

/**
 * Loads the front-page payload immediately, then hydrates its private images
 * when their signed URLs are available.
 *
 * A booking-event link narrows the payload here rather than in each step, so
 * every surface — services, locations, providers — offers only what the event
 * covers without knowing the event exists.
 */
export const useFrontPage = () => {
  const $ = _c(11);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      ...selfScheduleQueryOptions.frontPage()
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const frontPageQuery = useQuery(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = selfScheduleQueryOptions.frontPageImages();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = Boolean(frontPageQuery.data);
  let t3;
  if ($[2] !== t2) {
    t3 = {
      ...t1,
      enabled: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    data: frontPageImages
  } = useQuery(t3);
  const {
    data: bookingEvent
  } = useBookingEvent();
  let t4;
  if ($[4] !== bookingEvent || $[5] !== frontPageImages || $[6] !== frontPageQuery.data) {
    bb0: {
      if (!frontPageQuery.data) {
        t4 = frontPageQuery.data;
        break bb0;
      }
      const hydrated = frontPageImages ? {
        ...frontPageQuery.data,
        providers: frontPageQuery.data.providers.map(provider => ({
          ...provider,
          profileImage: frontPageImages.providerImages[provider.id] ?? provider.profileImage
        })),
        locations: frontPageQuery.data.locations.map(location => ({
          ...location,
          photoUrl: frontPageImages.locationImages[location.id] ?? location.photoUrl
        }))
      } : frontPageQuery.data;
      if (!bookingEvent) {
        t4 = hydrated;
        break bb0;
      }
      const eventServices = new Map(bookingEvent.services.map(_temp));
      t4 = {
        ...hydrated,
        services: hydrated.services.filter(service_0 => eventServices.has(service_0.id)).map(service_1 => {
          const priced = eventServices.get(service_1.id);
          if (!priced || priced.displayPrice == null) {
            return service_1;
          }
          const regular = priced.originalDisplayPrice;
          if (regular == null || priced.displayPrice >= regular) {
            return {
              ...service_1,
              displayPrice: priced.displayPrice,
              isEventPrice: true
            };
          }
          return {
            ...service_1,
            displayPrice: priced.displayPrice,
            originalDisplayPrice: regular,
            promotionName: bookingEvent.promotionName ?? `Save ${formatCurrency(regular - priced.displayPrice).replace(/\.00$/, "")}`,
            promotionDescription: bookingEvent.promotionDescription ?? null,
            promotionDiscountAmount: priced.promotionDiscountAmount ?? null,
            promotionDiscountPercentage: priced.promotionDiscountPercentage ?? null,
            isEventPrice: true
          };
        }),
        locations: bookingEvent.locationIds.length > 0 ? hydrated.locations.filter(location_0 => bookingEvent.locationIds.includes(location_0.id)) : hydrated.locations,
        providers: bookingEvent.providerId ? hydrated.providers.filter(provider_0 => provider_0.id === bookingEvent.providerId) : hydrated.providers
      };
    }
    $[4] = bookingEvent;
    $[5] = frontPageImages;
    $[6] = frontPageQuery.data;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const data = t4;
  let t5;
  if ($[8] !== data || $[9] !== frontPageQuery) {
    t5 = {
      ...frontPageQuery,
      data
    };
    $[8] = data;
    $[9] = frontPageQuery;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
function _temp(service) {
  return [service.id, service];
}